// Full-screen clue display. Lives over the board; when active, camera/board dim and a
// large card animates in with the prompt and a dollar ticker. Seconds-remaining
// signaling is owned by ui/countdown.ts (top-left widget).
//
// The prompt text is held invisible until `reveal_at_ms` (converted to local clock via
// the supplied `serverToLocal` function) so the clue is readable simultaneously with
// the streamer's viewers — see GDD §9 broadcast delay.

import {
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  type Texture,
} from 'three';
import type { Clue } from '@sg/shared';
import { clamp01, easeOutCubic } from './lib/easing.js';
import { createDollarTicker, type DollarTickerHandle } from './dollar-ticker.js';
import {
  COLOR_STAGE_BLUE,
  FONT_SLAB,
  STAGE_HEIGHT,
  STAGE_WIDTH,
} from './lib/theme.js';
import { textTexture } from './lib/text-texture.js';

export interface ClueZoomHandle {
  group: Object3D;
  setClue: (clue: Clue, revealAtLocalMs: number, hardEndLocalMs: number, graceEndLocalMs: number) => void;
  enterGrace: (graceEndLocalMs: number) => void;
  hide: () => void;
  tick: (nowMs: number) => void;
  dispose: () => void;
}

// Sized to clear the right-side leaderboard panel at stage x=[1500..1880].
// With the group offset to (-280, 0) — matching the board's left-shifted
// world position — a 1180-wide card lands at stage x≈[90..1270], leaving a
// comfortable gutter to the leaderboard. Old 1500-wide card centered at
// world origin overflowed under the leaderboard.
const CARD_W = 1180;
const CARD_H = 760;

export function createClueZoom(): ClueZoomHandle {
  const group = new Object3D();
  group.name = 'clue-zoom';
  group.visible = false;
  // Shift the entire clue overlay left so the wide card sits over the board
  // envelope rather than under the right-side leaderboard panel. Mirrors the
  // board's own world position (-280, -80) so the visual focus stays where
  // viewers were already looking. Y is centered (board has -80 vertical bias
  // for the side leaderboard alignment, but the clue card is the entire
  // attention so we leave it level).
  group.position.set(-280, 0, 0);

  // Scrim removed (Apr 2026): the dark backdrop used to fade in over
  // STAGE_WIDTH × STAGE_HEIGHT, but the right-side leaderboard panel renders
  // in DOM above the canvas and the scrim's edge cut a visible vertical seam
  // along the panel's left side (x=1500). The card carries its own opaque
  // background and the board is hidden via scene-visibility while the clue
  // zooms, so the scrim was purely decorative. Survey Says removed theirs
  // for the same reason in commit 75c0061. The variable is still declared
  // (as a tiny invisible plane) so dispose() / unused-import paths stay
  // unchanged downstream.
  const scrim = new Mesh(
    new PlaneGeometry(1, 1),
    new MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.0 }),
  );
  scrim.visible = false;
  scrim.position.z = -10;
  scrim.renderOrder = 10;
  group.add(scrim);

  const cardGeom = new PlaneGeometry(CARD_W, CARD_H);
  const cardMat = new MeshBasicMaterial({ color: COLOR_STAGE_BLUE, transparent: true });
  const card = new Mesh(cardGeom, cardMat);
  card.renderOrder = 20;
  group.add(card);

  const promptMat = new MeshBasicMaterial({ transparent: true, depthWrite: false, opacity: 0 });
  const prompt = new Mesh(new PlaneGeometry(CARD_W - 120, CARD_H - 300), promptMat);
  prompt.renderOrder = 30;
  prompt.position.z = 0.1;
  group.add(prompt);

  const ticker: DollarTickerHandle = createDollarTicker();
  ticker.mesh.position.set(0, -CARD_H / 2 + 140, 0.2);
  group.add(ticker.mesh);

  let currentClue: Clue | null = null;
  let revealAt = 0;
  let hardEnd = 0;
  let graceEnd = 0;
  let inGrace = false;
  let enterStart: number | null = null;

  function setClue(clue: Clue, revealAtLocalMs: number, hardEndLocalMs: number, graceEndLocalMs: number): void {
    currentClue = clue;
    // Defense in depth: if the clock-sync hands us a `revealAt` more than 30s
    // in the future or far in the past, the offset is corrupted (we've shipped
    // a regression on this twice). Fall back to "reveal now" so the prompt
    // stays readable instead of holding opacity 0 forever. Hard/grace ends
    // get clamped relative to performance.now() for the same reason.
    const now = performance.now();
    const skewMs = revealAtLocalMs - now;
    if (skewMs > 30_000 || skewMs < -30_000) {
      revealAt = now;
      hardEnd = now + 10_000;
      graceEnd = now + 15_000;
    } else {
      revealAt = revealAtLocalMs;
      hardEnd = hardEndLocalMs;
      graceEnd = graceEndLocalMs;
    }
    inGrace = false;
    enterStart = now;

    const tex: Texture = textTexture(clue.prompt, {
      font: `72px ${FONT_SLAB}`,
      color: '#FFFFFF',
      width: CARD_W - 120,
      height: CARD_H - 300,
      weight: 'bold',
      stroke: '#000000',
      strokeWidth: 3,
    });
    if (promptMat.map && promptMat.map !== tex) promptMat.map.dispose();
    promptMat.map = tex;
    promptMat.needsUpdate = true;
    promptMat.opacity = 0;

    ticker.arm(clue.value, revealAtLocalMs);
    group.visible = true;
  }

  function enterGrace(graceEndLocalMs: number): void {
    inGrace = true;
    graceEnd = graceEndLocalMs;
    ticker.lockToFloor();
  }

  function hide(): void {
    group.visible = false;
    currentClue = null;
    enterStart = null;
    ticker.reset();
  }

  function tick(nowMs: number): void {
    if (!group.visible) return;
    if (enterStart !== null) {
      const t = clamp01((nowMs - enterStart) / 1200);
      const e = easeOutCubic(t);
      // Scrim opacity intentionally untouched (kept at 0); see scrim setup
      // above for the rationale. Card scale tween is the only entry effect.
      card.scale.setScalar(0.6 + 0.4 * e);
      if (nowMs >= revealAt) {
        promptMat.opacity = clamp01((nowMs - revealAt) / 800);
      }
      if (t >= 1) enterStart = null;
    } else if (currentClue && nowMs >= revealAt) {
      promptMat.opacity = 1;
    }

    // Timer state captured for downstream use; the seconds display lives in
    // the top-left countdown widget, so the tick loop doesn't consume it.
    void hardEnd;
    void graceEnd;
    void inGrace;
    ticker.tick(nowMs);
  }

  return {
    group,
    setClue,
    enterGrace,
    hide,
    tick,
    dispose() {
      cardGeom.dispose();
      cardMat.dispose();
      promptMat.dispose();
      prompt.geometry.dispose();
      scrim.geometry.dispose();
      (scrim.material as MeshBasicMaterial).dispose();
      ticker.dispose();
    },
  };
}
